import { Position, type InternalNode, type Node } from "@xyflow/react";

function intersect(node: InternalNode<Node>, other: InternalNode<Node>) {
  const w = (node.measured.width ?? 0) / 2;
  const h = (node.measured.height ?? 0) / 2;
  const cx = node.internals.positionAbsolute.x + w;
  const cy = node.internals.positionAbsolute.y + h;
  const ox =
    other.internals.positionAbsolute.x + (other.measured.width ?? 0) / 2;
  const oy =
    other.internals.positionAbsolute.y + (other.measured.height ?? 0) / 2;

  const xx1 = (ox - cx) / (2 * w) - (oy - cy) / (2 * h);
  const yy1 = (ox - cx) / (2 * w) + (oy - cy) / (2 * h);
  const denom = Math.abs(xx1) + Math.abs(yy1);
  if (denom === 0) return { x: cx, y: cy };
  const a = 1 / denom;
  return { x: w * (a * xx1 + a * yy1) + cx, y: h * (a * yy1 - a * xx1) + cy };
}

function sideOf(
  node: InternalNode<Node>,
  point: { x: number; y: number },
): Position {
  const x = Math.round(node.internals.positionAbsolute.x);
  const y = Math.round(node.internals.positionAbsolute.y);
  const w = node.measured.width ?? 0;
  const px = Math.round(point.x);
  const py = Math.round(point.y);
  if (px <= x + 1) return Position.Left;
  if (px >= x + w - 1) return Position.Right;
  if (py <= y + 1) return Position.Top;
  return Position.Bottom;
}

export function getEdgeParams(
  source: InternalNode<Node>,
  target: InternalNode<Node>,
) {
  const sp = intersect(source, target);
  const tp = intersect(target, source);
  return {
    sx: sp.x,
    sy: sp.y,
    tx: tp.x,
    ty: tp.y,
    sourcePos: sideOf(source, sp),
    targetPos: sideOf(target, tp),
  };
}
